import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Hospitality.css";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getPublicSectors } from "../../../../api/sectors";

import img1 from "../../../../assets/images/home/hospitality/c1.jpg";
import img2 from "../../../../assets/images/home/hospitality/c2.jpg";
import img3 from "../../../../assets/images/home/hospitality/c3.jpg";
import img4 from "../../../../assets/images/home/hospitality/c4.png";
import { resolveMediaUrl } from "../../../../utils/media";

const fallbackHospitality = {
  tag: "SECTORS",
  titleLine1: "Key Sectors",
  titleLine2: "We Serve",
  item1Title: "COMMERCIAL",
  item1Image: img1,
  item1Link: "/sectors/commercial",
  item2Title: "RESIDENTIAL",
  item2Image: img2,
  item2Link: "/sectors/residential",
  item3Title: "ENTERTAINMENT",
  item3Image: img3,
  item3Link: "/sectors/entertainment",
  item4Title: "HOSPITALITY",
  item4Image: img4,
  item4Link: "/sectors/hospitality",
};

const itemFieldRegex = /^item(\d+)([a-zA-Z0-9_]+)$/i;

const hasSectionData = (section) => {
  if (!section || typeof section !== "object") return false;
  return Object.values(section).some((value) => {
    if (value === null || typeof value === "undefined") return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
};

const getItemImageFromApi = (content, itemNumber) => {
  const suffix = String(itemNumber);
  const candidates = [
    content?.[`item${suffix}Image`],
    content?.[`item${suffix}image`],
    content?.[`item${suffix}Img`],
    content?.[`item${suffix}img`],
    content?.[`item${suffix}Icon`],
    content?.[`item${suffix}icon`],
    content?.[`item${suffix}Media`],
    content?.[`item${suffix}media`],
    content?.[`item${suffix}MediaUrl`],
    content?.[`item${suffix}mediaUrl`],
    content?.[`item${suffix}Photo`],
    content?.[`item${suffix}photo`],
  ];

  for (const candidate of candidates) {
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

/** Match previous slider: repeat first cards so the row scrolls comfortably. */
const extendSectorsForSlider = (items) => {
  if (!items.length) return items;
  if (items.length >= 7) return items;
  return [...items, ...items.slice(0, Math.min(3, items.length))];
};

const Hospitality = ({ content }) => {
  const { i18n } = useTranslation();
  const sliderRef = useRef(null);
  const dragRef = useRef({
    isMouseDown: false,
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
  });
  const [apiSectors, setApiSectors] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      const language = String(i18n.language || "en").toLowerCase();
      try {
        const data = await getPublicSectors(language);
        if (isMounted) {
          setApiSectors(Array.isArray(data) ? data : []);
        }
      } catch {
        if (isMounted) {
          setApiSectors([]);
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const hasApiContent = hasSectionData(content);
  const sectionContent = hasApiContent ? content : fallbackHospitality;

  const sectors = useMemo(() => {
    const apiMapped = (Array.isArray(apiSectors) ? apiSectors : [])
      .map((item, index) => {
        const slug = String(item?.slug || "").trim().toLowerCase();
        const title = String(item?.title || item?.name || "").trim();
        const image = resolveMediaUrl(
          item?.image || item?.imageUrl || item?.coverImageUrl || item?.heroImageUrl || item?.mediaUrl
        );
        const fallbackImage = [img1, img2, img3, img4][index % 4];
        if (!title) return null;
        return {
          title,
          image: image || fallbackImage,
          fallbackImage,
          to: slug ? `/sectors/${slug}` : "/sectors",
        };
      })
      .filter(Boolean);

    if (apiMapped.length) {
      return apiMapped;
    }

    const cardsMap = new Map();
    Object.entries(sectionContent || {}).forEach(([key, value]) => {
      const match = key.match(itemFieldRegex);
      if (!match) return;
      const index = Number(match[1]);
      const field = String(match[2] || "").replace(/[_\s-]+/g, "").toLowerCase();
      if (!cardsMap.has(index)) cardsMap.set(index, {});
      cardsMap.get(index)[field] = value;
    });

    const fallbackImages = [img1, img2, img3, img4];
    const mapped = Array.from(cardsMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([index, fields], idx) => {
        const resolvedImage = hasApiContent
          ? getItemImageFromApi(sectionContent, index) ||
            resolveMediaUrl(fields.image || fields.img || fields.icon || fields.media || fields.mediaurl) ||
            ""
          : fields.image || fields.img || fields.icon || fields.media || fields.mediaurl || "";

        const fallbackImage = fallbackImages[idx % fallbackImages.length];

        return {
          title: (fields.title || "").trim() || fallbackHospitality[`item${index}Title`] || "",
          image: resolvedImage || fallbackImage,
          fallbackImage,
          to:
            fields.link ||
            fields.url ||
            fields.to ||
            fallbackHospitality[`item${index}Link`] ||
            "/sectors/commercial",
        };
      })
      .filter((item) => item.title || item.to || item.image);

    const base =
      mapped.length > 0
        ? mapped
        : [
            {
              title: fallbackHospitality.item1Title,
              image: fallbackHospitality.item1Image,
              fallbackImage: img1,
              to: fallbackHospitality.item1Link,
            },
            {
              title: fallbackHospitality.item2Title,
              image: fallbackHospitality.item2Image,
              fallbackImage: img2,
              to: fallbackHospitality.item2Link,
            },
            {
              title: fallbackHospitality.item3Title,
              image: fallbackHospitality.item3Image,
              fallbackImage: img3,
              to: fallbackHospitality.item3Link,
            },
            {
              title: fallbackHospitality.item4Title,
              image: fallbackHospitality.item4Image,
              fallbackImage: img4,
              to: fallbackHospitality.item4Link,
            },
          ];

    return extendSectorsForSlider(base);
  }, [sectionContent, hasApiContent, apiSectors]);

  const handleMouseDown = (e) => {
    if (!sliderRef.current) return;

    dragRef.current.isMouseDown = true;
    dragRef.current.isDragging = false;
    dragRef.current.startX = e.clientX;
    dragRef.current.startScrollLeft = sliderRef.current.scrollLeft;

    sliderRef.current.classList.add("is-dragging");
  };

  const handleMouseMove = (e) => {
    if (!sliderRef.current || !dragRef.current.isMouseDown) return;

    const delta = e.clientX - dragRef.current.startX;
    if (Math.abs(delta) > 14) {
      dragRef.current.isDragging = true;
    }

    if (!dragRef.current.isDragging) return;

    e.preventDefault();
    sliderRef.current.scrollLeft = dragRef.current.startScrollLeft - delta * 1.2;
  };

  const stopDrag = () => {
    if (!sliderRef.current) return;

    dragRef.current.isMouseDown = false;
    dragRef.current.isDragging = false;
    sliderRef.current.classList.remove("is-dragging");
  };

  return (
    <section className="hospitality">
      <div className="hospitality-container">
        <span className="hospitality-tag tag">SECTORS</span>

        <h2>
          {sectionContent.titleLine1 || fallbackHospitality.titleLine1}{" "}
          <span>{sectionContent.titleLine2 || fallbackHospitality.titleLine2}</span>
        </h2>

        <div
          className="hospitality-slider"
          ref={sliderRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
        >
          {sectors.map((item, index) => (
            <Link
              className="hospitality-card hospitality-cardLink"
              key={`${item.to}-${item.title}-${index}`}
              to={item.to}
            >
              <div className="hospitality-image">
                <img
                  src={item.image || item.fallbackImage}
                  alt={item.title}
                  draggable={false}
                  onError={(e) => {
                    if (e.currentTarget.src !== item.fallbackImage) {
                      e.currentTarget.src = item.fallbackImage;
                    }
                  }}
                />
              </div>

              <h3>{item.title}</h3>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hospitality;
